import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@mui/material';
import {
  AirplanemodeActive,
  Assessment,
  Assignment,
  Build,
  Engineering,
  Inventory2,
  ShoppingCart,
  Timeline
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { needsStoreIntentAction } from '../../utils/workOrderStoreReview';

const THEME = {
  ink: '#0b1f33',
  navy: '#132a43',
  steel: '#3d5a80',
  mist: '#e8eef5',
  card: '#ffffff',
  border: '#d5dee9',
  gold: '#9a7b4f',
  goldSoft: '#c4a574',
  success: '#1b6b4a',
  warning: '#a16207',
  danger: '#9b2c2c',
  info: '#1d4f7c',
  muted: '#5c6b7a'
};

const DEPT_COLORS = {
  operations: '#1d4f7c',
  sales: '#9a7b4f',
  camo: '#3d5a80',
  planning: '#4a6fa5',
  store: '#a16207',
  mro: '#1b6b4a'
};

const ACTIVE_OPS_TRIP = new Set([
  'PENDING_OPS',
  'CREW_ASSIGNMENT',
  'ASSIGNED',
  'CREW_ACCEPTED',
  'CREW_REJECTED',
  'READY_FOR_BA',
  'READY_FOR_ATC',
  'CLEARED'
]);
const SALES_PIPELINE = new Set(['DRAFT', 'PENDING_OPS', ...ACTIVE_OPS_TRIP]);
const CLOSED_WO = new Set(['COMPLETED', 'CANCELLED']);
const OPEN_PO = new Set([
  'DRAFT',
  'PENDING_STORE_APPROVAL',
  'PENDING_AM_APPROVAL',
  'ISSUED',
  'PARTIALLY_RECEIVED'
]);

const countBy = (items, keyFn) => {
  const map = {};
  (items || []).forEach((item) => {
    const key = keyFn(item) || 'Unknown';
    map[key] = (map[key] || 0) + 1;
  });
  return map;
};

const toPie = (map, palette) =>
  Object.entries(map)
    .filter(([, v]) => v > 0)
    .map(([name, value], i) => ({
      name: String(name).replace(/_/g, ' '),
      value,
      color: palette[i % palette.length]
    }));

const ratingMeta = (score) => {
  if (score >= 85) return { label: 'Excellent', color: THEME.success };
  if (score >= 70) return { label: 'Good', color: THEME.info };
  if (score >= 55) return { label: 'Fair', color: THEME.warning };
  return { label: 'Needs Attention', color: THEME.danger };
};

/**
 * Score blends throughput vs backlog pressure, with extra penalty for critical queues.
 */
const computeScore = ({ volume = 0, completed = 0, backlog = 0, critical = 0 }) => {
  if (volume === 0 && backlog === 0 && critical === 0) {
    return 100;
  }
  const completionRate = volume > 0 ? completed / volume : completed > 0 ? 1 : 0;
  const backlogRatio = volume > 0 ? Math.min(1.5, backlog / volume) / 1.5 : backlog > 0 ? 1 : 0;
  const criticalPenalty = Math.min(35, critical * 5);
  return Math.round(
    Math.max(
      0,
      Math.min(100, completionRate * 50 + (1 - backlogRatio) * 50 - criticalPenalty)
    )
  );
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1,
        border: `1px solid ${THEME.border}`,
        bgcolor: THEME.card,
        borderRadius: 1.5,
        boxShadow: `0 8px 24px ${alpha(THEME.ink, 0.12)}`
      }}
    >
      <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }}>
        {item.name || item.payload?.name || item.payload?.department}
      </Typography>
      <Typography variant="subtitle2" sx={{ color: THEME.ink, fontWeight: 700 }}>
        {item.value}
        {item.payload?.unit ? ` ${item.payload.unit}` : ''}
      </Typography>
    </Paper>
  );
};

const StatusPie = ({ data, height = 220 }) => {
  if (!data?.length) {
    return (
      <Box
        sx={{
          height,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 2,
          bgcolor: THEME.mist
        }}
      >
        <Typography variant="body2" sx={{ color: THEME.muted }}>
          No status volume to chart.
        </Typography>
      </Box>
    );
  }
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <Box sx={{ height, position: 'relative' }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="46%"
            innerRadius={48}
            outerRadius={72}
            paddingAngle={2}
            stroke={THEME.card}
            strokeWidth={2}
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
          <RechartsTooltip content={<ChartTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={40}
            formatter={(value) => (
              <span style={{ color: THEME.muted, fontSize: 11 }}>{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
      <Box
        sx={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none'
        }}
      >
        <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }}>
          Total
        </Typography>
        <Typography variant="h6" sx={{ color: THEME.ink, fontWeight: 700, lineHeight: 1 }}>
          {total}
        </Typography>
      </Box>
    </Box>
  );
};

const MetricRow = ({ label, value, accent = THEME.steel }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 0.4 }}>
    <Typography variant="body2" sx={{ color: THEME.muted }}>
      {label}
    </Typography>
    <Typography
      variant="subtitle2"
      sx={{ color: accent, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
  </Stack>
);

const ALL_AIRCRAFT = 'ALL';

const toId = (ref) => {
  if (ref == null || ref === '') return null;
  if (typeof ref === 'object') {
    // Prefer nested identifiers (populated docs, mongoose-like objects).
    if (ref._id != null && ref._id !== ref) return toId(ref._id);
    if (ref.id != null && ref.id !== ref) return toId(ref.id);
    return null;
  }
  const asString = String(ref).trim();
  if (!asString || asString === '[object Object]') return null;
  return asString;
};

const toReg = (value) => {
  if (value == null || value === '') return null;
  const asString = String(value).trim().toUpperCase();
  return asString || null;
};

const collectAircraftKeys = (entity, ids, regs) => {
  if (!entity || typeof entity !== 'object') return;

  const addRef = (ref) => {
    const id = toId(ref);
    if (id) ids.add(id);
    if (ref && typeof ref === 'object') {
      const reg = toReg(ref.registration || ref.tailNumber);
      if (reg) regs.add(reg);
    }
  };

  addRef(entity.aircraftId);
  addRef(entity.listAircraftId);
  addRef(entity.aircraft);

  const reg = toReg(entity.aircraftRegistration || entity.registration);
  if (reg) regs.add(reg);

  if (Array.isArray(entity.items)) {
    entity.items.forEach((item) => collectAircraftKeys(item, ids, regs));
  }
  if (Array.isArray(entity.sectors)) {
    entity.sectors.forEach((sector) => collectAircraftKeys(sector, ids, regs));
  }
};

const matchesAircraftFields = (entity, aircraftId, aircraftReg) => {
  if (!entity) return false;
  const ids = new Set();
  const regs = new Set();
  collectAircraftKeys(entity, ids, regs);

  const wantedId = toId(aircraftId);
  const wantedReg = toReg(aircraftReg);

  if (wantedId && ids.has(wantedId)) return true;
  if (wantedReg && regs.has(wantedReg)) return true;
  return false;
};

const filterInventoryList = (list, aircraftId, aircraftReg) =>
  (list || []).filter((item) => matchesAircraftFields(item, aircraftId, aircraftReg));

/**
 * Scope department analysis datasets to one aircraft (or return fleet-wide when ALL).
 */
const filterSourceByAircraft = (source, aircraftId, aircraftReg) => {
  const wantedId = toId(aircraftId);
  if (!wantedId || wantedId === ALL_AIRCRAFT) return source;
  const wantedReg = toReg(aircraftReg);

  const sectors = (source.sectors || []).filter((s) =>
    matchesAircraftFields(s, wantedId, wantedReg)
  );
  const tripIds = new Set(sectors.map((s) => toId(s.tripId)).filter(Boolean));

  const trips = (source.trips || []).filter((t) => {
    if (matchesAircraftFields(t, wantedId, wantedReg)) return true;
    const tripId = toId(t._id || t.id);
    return tripId ? tripIds.has(tripId) : false;
  });

  const maintenance = (source.maintenance || []).filter((m) =>
    matchesAircraftFields(m, wantedId, wantedReg)
  );
  const purchaseOrders = (source.purchaseOrders || []).filter((p) =>
    matchesAircraftFields(p, wantedId, wantedReg)
  );
  const receiving = (source.receiving || []).filter((r) =>
    matchesAircraftFields(r, wantedId, wantedReg)
  );
  const intimations = (source.intimations || []).filter((i) =>
    matchesAircraftFields(i, wantedId, wantedReg)
  );
  const technicalLogBooks = (source.technicalLogBooks || []).filter((t) =>
    matchesAircraftFields(t, wantedId, wantedReg)
  );
  const storeAlerts = {
    lowStock: filterInventoryList(source.storeAlerts?.lowStock, wantedId, wantedReg),
    expiringItems: filterInventoryList(source.storeAlerts?.expiringItems, wantedId, wantedReg),
    calibrationDue: filterInventoryList(source.storeAlerts?.calibrationDue, wantedId, wantedReg)
  };

  return {
    trips,
    sectors,
    maintenance,
    purchaseOrders,
    receiving,
    intimations,
    technicalLogBooks,
    storeAlerts
  };
};

const buildDepartments = ({
  trips = [],
  sectors = [],
  maintenance = [],
  purchaseOrders = [],
  receiving = [],
  intimations = [],
  technicalLogBooks = [],
  storeAlerts = { lowStock: [], expiringItems: [], calibrationDue: [] }
}) => {
  const statusPalette = [
    THEME.steel,
    THEME.gold,
    THEME.info,
    THEME.success,
    THEME.warning,
    THEME.danger,
    '#6b5b95',
    '#4a7c59'
  ];

  // —— Sales (Sales Enquiry) ——
  const salesByStatus = countBy(trips, (t) => t.status);
  const salesDraft = salesByStatus.DRAFT || 0;
  const salesPendingOps = salesByStatus.PENDING_OPS || 0;
  const salesCompleted =
    (salesByStatus.COMPLETED || 0) + (salesByStatus.CHARTER_COMPLETED || 0);
  const salesActive = trips.filter((t) => SALES_PIPELINE.has(t.status) && t.status !== 'DRAFT')
    .length;
  const salesVolume = trips.length;
  const salesBacklog = salesDraft + salesPendingOps;
  const salesScore = computeScore({
    volume: salesVolume,
    completed: salesCompleted,
    backlog: salesBacklog,
    critical: salesPendingOps
  });

  // —— Operations ——
  const opsPendingTrips = salesPendingOps;
  const sectorByStatus = countBy(sectors, (s) => s.status);
  const baQueue = sectorByStatus.READY_FOR_BA || 0;
  const atcQueue = sectorByStatus.READY_FOR_ATC || 0;
  const cleared = sectorByStatus.CLEARED || 0;
  const sectorDone =
    (sectorByStatus.SECTOR_COMPLETED || 0) + (sectorByStatus.COMPLETED || 0);
  const opsActiveTrips = trips.filter((t) => ACTIVE_OPS_TRIP.has(t.status)).length;
  const opsBacklog = opsPendingTrips + baQueue + atcQueue;
  const opsVolume = Math.max(sectors.length, opsActiveTrips + opsPendingTrips, 1);
  const opsScore = computeScore({
    volume: opsVolume,
    completed: sectorDone,
    backlog: opsBacklog,
    critical: opsPendingTrips + baQueue + atcQueue
  });

  // —— CAMO ——
  const camoTripApprovals = trips.filter(
    (t) =>
      t.status === 'CREW_ASSIGNMENT' &&
      t.approvals?.accountableManager?.approved === true &&
      t.approvals?.camo?.approved !== true
  ).length;
  const camoQmPending = maintenance.filter(
    (m) => m.status === 'SCHEDULED' && m.camoQmAcknowledged !== true
  ).length;
  const camoQmDone = maintenance.filter((m) => m.camoQmAcknowledged === true).length;
  const camoTripDone = trips.filter((t) => t.approvals?.camo?.approved === true).length;
  const activeIntimations = intimations.filter((i) => i.status !== 'ARCHIVED');
  const intimationPendingResponse = activeIntimations.filter((i) => i.status === 'PENDING').length;
  const intimationClarificationRequested = activeIntimations.filter(
    (i) => i.status === 'CLARIFICATION_REQUESTED'
  ).length;
  const intimationAcknowledged = activeIntimations.filter((i) => i.status === 'ACKNOWLEDGED').length;
  const intimationCompleted = activeIntimations.filter((i) => i.status === 'COMPLETED').length;
  const intimationResponded = intimationAcknowledged + intimationCompleted;
  const tlbByStatus = countBy(technicalLogBooks, (e) => e.workflowStatus);
  const tlbSubmitted = tlbByStatus.SUBMITTED || 0;
  const tlbUnderReview = tlbByStatus.UNDER_REVIEW || 0;
  const tlbApproved = tlbByStatus.APPROVED || 0;
  const tlbRejected = tlbByStatus.REJECTED || 0;
  const tlbDraft = tlbByStatus.DRAFT || 0;
  const tlbApprovalPending = tlbSubmitted + tlbUnderReview;
  const camoBacklog =
    camoTripApprovals + camoQmPending + intimationPendingResponse + tlbApprovalPending;
  const camoVolume = Math.max(
    trips.filter((t) => t.status === 'CREW_ASSIGNMENT').length +
      maintenance.filter((m) => m.status === 'SCHEDULED').length +
      activeIntimations.length +
      technicalLogBooks.length,
    camoBacklog + camoQmDone + intimationResponded + tlbApproved,
    1
  );
  const camoScore = computeScore({
    volume: camoVolume,
    completed: camoQmDone + camoTripDone + intimationResponded + tlbApproved,
    backlog: camoBacklog,
    critical: camoBacklog
  });

  // —— Planning ——
  const planningPending = maintenance.filter(
    (m) =>
      m.status === 'SCHEDULED' &&
      m.camoQmAcknowledged === true &&
      m.planningApproved !== true &&
      !m.planningRejectedAt
  ).length;
  const planningDone = maintenance.filter((m) => m.planningApproved === true).length;
  const planningRejected = maintenance.filter((m) => Boolean(m.planningRejectedAt)).length;
  const planningClarificationReplies = intimationClarificationRequested;
  const planningClarificationReplied = activeIntimations.filter(
    (i) => Boolean(i.planningClarificationReply)
  ).length;
  const intimationsCreatedByPlanning = activeIntimations.length;
  const intimationsCompletedForPlanning = activeIntimations.filter((i) =>
    ['ACKNOWLEDGED', 'COMPLETED'].includes(i.status)
  ).length;
  const planningCompletedWork =
    planningDone + intimationsCompletedForPlanning + planningClarificationReplied;
  const planningBacklog = planningPending + planningClarificationReplies;
  const planningVolume = Math.max(
    maintenance.filter((m) => m.camoQmAcknowledged === true || m.planningApproved).length +
      intimationsCreatedByPlanning,
    planningPending + planningCompletedWork,
    1
  );
  const planningScore = computeScore({
    volume: planningVolume,
    completed: planningCompletedWork,
    backlog: planningBacklog,
    critical: planningPending + planningRejected
  });

  // —— Procurement & Store ——
  const poByStatus = countBy(purchaseOrders, (p) => p.status);
  const openPos = purchaseOrders.filter((p) => OPEN_PO.has(p.status)).length;
  const closedPos = purchaseOrders.filter((p) =>
    ['RECEIVED', 'CLOSED', 'CANCELLED'].includes(p.status)
  ).length;
  const receivingPending = receiving.filter(
    (r) =>
      r.workflowStatus === 'PENDING_INSPECTOR_APPROVAL' ||
      r.workflowStatus === 'SUBMITTED' ||
      r.workflowStatus === 'DRAFT'
  ).length;
  const storeWoActions = maintenance.filter(
    (m) =>
      m.status === 'SCHEDULED' &&
      m.planningApproved === true &&
      needsStoreIntentAction(m)
  ).length;
  const lowStock = storeAlerts.lowStock?.length || 0;
  const expiring = storeAlerts.expiringItems?.length || 0;
  const calibration = storeAlerts.calibrationDue?.length || 0;
  const storeCritical = lowStock + expiring + calibration + storeWoActions;
  const storeBacklog = openPos + receivingPending + storeWoActions;
  const storeVolume = Math.max(purchaseOrders.length + receiving.length, storeBacklog, 1);
  const storeScore = computeScore({
    volume: storeVolume,
    completed: closedPos,
    backlog: storeBacklog,
    critical: storeCritical
  });

  // —— MRO ——
  const mroByStatus = countBy(
    maintenance.filter((m) => !CLOSED_WO.has(m.status) || m.status === 'COMPLETED'),
    (m) => m.status
  );
  const mroAcceptPending = maintenance.filter(
    (m) =>
      m.status === 'SCHEDULED' &&
      m.planningApproved === true &&
      m.needsMROAction === true &&
      !m.mroAccepted
  ).length;
  const mroInProgress = maintenance.filter((m) => m.status === 'IN_PROGRESS').length;
  const mroCompleted = maintenance.filter((m) => m.status === 'COMPLETED').length;
  const mroScheduled = maintenance.filter((m) => m.status === 'SCHEDULED').length;
  const mroBacklog = mroAcceptPending + mroInProgress;
  const mroVolume = Math.max(maintenance.length, mroBacklog + mroCompleted, 1);
  const mroScore = computeScore({
    volume: mroVolume,
    completed: mroCompleted,
    backlog: mroBacklog,
    critical: mroAcceptPending
  });

  const departments = [
    {
      id: 'operations',
      name: 'Operations',
      scope: 'Operations board, sector clearances, BA & ATC queues',
      color: DEPT_COLORS.operations,
      icon: <Assignment fontSize="small" />,
      score: opsScore,
      metrics: [
        { label: 'Trips awaiting ops', value: opsPendingTrips },
        { label: 'Active ops trips', value: opsActiveTrips },
        { label: 'BA clearances pending', value: baQueue },
        { label: 'ATC clearances pending', value: atcQueue },
        { label: 'Cleared sectors', value: cleared },
        { label: 'Completed sectors', value: sectorDone }
      ],
      statusPie: toPie(sectorByStatus, statusPalette),
      barMetrics: [
        { name: 'Pending Ops', value: opsPendingTrips, fill: THEME.warning },
        { name: 'BA Queue', value: baQueue, fill: THEME.gold },
        { name: 'ATC Queue', value: atcQueue, fill: THEME.info },
        { name: 'Cleared', value: cleared, fill: THEME.steel },
        { name: 'Completed', value: sectorDone, fill: THEME.success }
      ]
    },
    {
      id: 'sales',
      name: 'Sales',
      scope: 'Sales enquiry pipeline from draft through charter completion',
      color: DEPT_COLORS.sales,
      icon: <ShoppingCart fontSize="small" />,
      score: salesScore,
      metrics: [
        { label: 'Total enquiries', value: salesVolume },
        { label: 'Drafts', value: salesDraft },
        { label: 'Sent to operations', value: salesPendingOps },
        { label: 'Active pipeline', value: salesActive },
        { label: 'Completed charters', value: salesCompleted },
        { label: 'Cancelled / grounded', value: (salesByStatus.CANCELLED || 0) + (salesByStatus.GROUNDED || 0) }
      ],
      statusPie: toPie(salesByStatus, statusPalette),
      barMetrics: [
        { name: 'Draft', value: salesDraft, fill: THEME.muted },
        { name: 'Pending Ops', value: salesPendingOps, fill: THEME.warning },
        { name: 'In Pipeline', value: salesActive, fill: THEME.info },
        { name: 'Completed', value: salesCompleted, fill: THEME.success }
      ]
    },
    {
      id: 'camo',
      name: 'CAMO',
      scope:
        'Charter CAMO approvals, techlog approvals, work-order QM acknowledgments, and responding to planning intimations',
      color: DEPT_COLORS.camo,
      icon: <Assessment fontSize="small" />,
      score: camoScore,
      metrics: [
        { label: 'Charter CAMO approvals pending', value: camoTripApprovals },
        { label: 'Techlog approvals pending', value: tlbApprovalPending },
        { label: 'Techlogs submitted', value: tlbSubmitted },
        { label: 'Techlogs under review', value: tlbUnderReview },
        { label: 'Techlogs approved', value: tlbApproved },
        { label: 'Techlogs rejected', value: tlbRejected },
        { label: 'Techlogs in draft', value: tlbDraft },
        { label: 'CAMO QM acknowledgments pending', value: camoQmPending },
        { label: 'Intimations awaiting CAMO response', value: intimationPendingResponse },
        { label: 'Clarification requested by CAMO', value: intimationClarificationRequested },
        { label: 'Intimations acknowledged / completed', value: intimationResponded },
        { label: 'QM acknowledgments done', value: camoQmDone },
        { label: 'Charters with CAMO approved', value: camoTripDone },
        { label: 'Open backlog', value: camoBacklog }
      ],
      statusPie: toPie(
        {
          'CAMO Trip Pending': camoTripApprovals,
          'TLB Submitted': tlbSubmitted,
          'TLB Under Review': tlbUnderReview,
          'TLB Approved': tlbApproved,
          'TLB Rejected': tlbRejected,
          'QM Ack Pending': camoQmPending,
          'Intimation Pending': intimationPendingResponse,
          'Clarification Asked': intimationClarificationRequested,
          'Intimation Done': intimationResponded,
          'QM Ack Done': camoQmDone,
          'Trip CAMO Done': camoTripDone
        },
        statusPalette
      ),
      barMetrics: [
        { name: 'Trip Approvals', value: camoTripApprovals, fill: THEME.warning },
        { name: 'TLB Pending', value: tlbApprovalPending, fill: THEME.danger },
        { name: 'TLB Approved', value: tlbApproved, fill: THEME.success },
        { name: 'QM Pending', value: camoQmPending, fill: THEME.gold },
        { name: 'Intimation Pending', value: intimationPendingResponse, fill: '#9b2c2c' },
        { name: 'Clarification', value: intimationClarificationRequested, fill: '#6b5b95' },
        { name: 'Intimation Done', value: intimationResponded, fill: THEME.info },
        { name: 'QM Done', value: camoQmDone, fill: THEME.steel },
        { name: 'Trip Done', value: camoTripDone, fill: '#4a7c59' }
      ]
    },
    {
      id: 'planning',
      name: 'Planning',
      scope:
        'Maintenance planning approvals, intimations, clarification replies, and completed planning throughput',
      color: DEPT_COLORS.planning,
      icon: <Timeline fontSize="small" />,
      score: planningScore,
      metrics: [
        { label: 'Planning approvals pending', value: planningPending },
        { label: 'Work orders planning approved', value: planningDone },
        { label: 'Planning rejected', value: planningRejected },
        { label: 'Intimations created (active)', value: intimationsCreatedByPlanning },
        { label: 'Intimations acknowledged / completed', value: intimationsCompletedForPlanning },
        { label: 'Clarification replies sent', value: planningClarificationReplied },
        { label: 'Clarification replies pending', value: planningClarificationReplies },
        { label: 'Completed planning work', value: planningCompletedWork },
        { label: 'Open backlog', value: planningBacklog }
      ],
      statusPie: toPie(
        {
          'WO Planning Pending': planningPending,
          'WO Planning Approved': planningDone,
          'Intimations Done': intimationsCompletedForPlanning,
          'Clarification Sent': planningClarificationReplied,
          'Clarification Pending': planningClarificationReplies,
          Rejected: planningRejected
        },
        statusPalette
      ),
      barMetrics: [
        { name: 'WO Pending', value: planningPending, fill: THEME.warning },
        { name: 'WO Approved', value: planningDone, fill: THEME.success },
        { name: 'Intimations Done', value: intimationsCompletedForPlanning, fill: THEME.info },
        { name: 'Clarification Sent', value: planningClarificationReplied, fill: '#4a7c59' },
        { name: 'Clarification Pending', value: planningClarificationReplies, fill: '#6b5b95' },
        { name: 'Rejected', value: planningRejected, fill: THEME.muted }
      ]
    },
    {
      id: 'store',
      name: 'Procurement & Store',
      scope: 'Purchase orders, receiving, inventory alerts, and store WO actions',
      color: DEPT_COLORS.store,
      icon: <Inventory2 fontSize="small" />,
      score: storeScore,
      metrics: [
        { label: 'Open purchase orders', value: openPos },
        { label: 'Closed / received POs', value: closedPos },
        { label: 'Receiving in progress', value: receivingPending },
        { label: 'Store WO actions pending', value: storeWoActions },
        { label: 'Low stock items', value: lowStock },
        { label: 'Expiring items', value: expiring },
        { label: 'Calibration due', value: calibration }
      ],
      statusPie: toPie(
        Object.keys(poByStatus).length
          ? poByStatus
          : {
              'Open POs': openPos,
              'Receiving': receivingPending,
              'Store WO': storeWoActions,
              'Low Stock': lowStock,
              Expiring: expiring,
              Calibration: calibration
            },
        statusPalette
      ),
      barMetrics: [
        { name: 'Open POs', value: openPos, fill: THEME.gold },
        { name: 'Receiving', value: receivingPending, fill: THEME.info },
        { name: 'Store WO', value: storeWoActions, fill: THEME.steel },
        { name: 'Low Stock', value: lowStock, fill: THEME.warning },
        { name: 'Expiring', value: expiring, fill: THEME.danger },
        { name: 'Calibration', value: calibration, fill: '#6b5b95' }
      ]
    },
    {
      id: 'mro',
      name: 'MRO',
      scope: 'Work-order acceptance, execution, and completion',
      color: DEPT_COLORS.mro,
      icon: <Engineering fontSize="small" />,
      score: mroScore,
      metrics: [
        { label: 'Awaiting MRO acceptance', value: mroAcceptPending },
        { label: 'In progress', value: mroInProgress },
        { label: 'Scheduled (open)', value: mroScheduled },
        { label: 'Completed', value: mroCompleted },
        { label: 'Open backlog', value: mroBacklog }
      ],
      statusPie: toPie(
        {
          'Awaiting Accept': mroAcceptPending,
          'In Progress': mroInProgress,
          Scheduled: Math.max(0, mroScheduled - mroAcceptPending),
          Completed: mroCompleted,
          ...(mroByStatus.CANCELLED ? { Cancelled: mroByStatus.CANCELLED } : {})
        },
        statusPalette
      ),
      barMetrics: [
        { name: 'Awaiting Accept', value: mroAcceptPending, fill: THEME.warning },
        { name: 'In Progress', value: mroInProgress, fill: THEME.info },
        { name: 'Scheduled', value: mroScheduled, fill: THEME.steel },
        { name: 'Completed', value: mroCompleted, fill: THEME.success }
      ]
    }
  ];

  return departments.map((d) => ({
    ...d,
    ...ratingMeta(d.score)
  }));
};

const DepartmentWiseAnalysis = ({
  trips = [],
  sectors = [],
  maintenance = [],
  purchaseOrders = [],
  receiving = [],
  intimations = [],
  technicalLogBooks = [],
  aircraft = [],
  storeAlerts,
  analysisLoading = false
}) => {
  const [aircraftFilter, setAircraftFilter] = useState(ALL_AIRCRAFT);
  const [tab, setTab] = useState(0);
  const [aircraftScoreboard, setAircraftScoreboard] = useState([]);
  const [scoreboardLoading, setScoreboardLoading] = useState(false);

  const fleetSource = useMemo(
    () => ({
      trips,
      sectors,
      maintenance,
      purchaseOrders,
      receiving,
      intimations,
      technicalLogBooks,
      storeAlerts
    }),
    [
      trips,
      sectors,
      maintenance,
      purchaseOrders,
      receiving,
      intimations,
      technicalLogBooks,
      storeAlerts
    ]
  );

  const aircraftOptions = useMemo(() => {
    const map = new Map();
    const add = (id, reg, status = null) => {
      const normalizedId = toId(id);
      if (!normalizedId) return;
      const displayReg = reg != null && String(reg).trim() ? String(reg).trim() : null;
      const prev = map.get(normalizedId);
      if (!prev) {
        map.set(normalizedId, {
          id: normalizedId,
          registration: displayReg || normalizedId,
          status
        });
        return;
      }
      if (displayReg && (prev.registration === prev.id || !prev.registration)) {
        prev.registration = displayReg;
      }
      if (!prev.status && status) prev.status = status;
    };

    (aircraft || []).forEach((a) => {
      add(a?._id || a?.id, a?.registration || a?.tailNumber, a?.status);
    });

    // Always merge ids seen in operational data so filter keys stay aligned.
    [...maintenance, ...intimations, ...technicalLogBooks, ...sectors].forEach(
      (row) => {
        add(
          row?.aircraftId?._id || row?.aircraftId,
          row?.aircraftRegistration || row?.aircraftId?.registration || row?.aircraftId?.tailNumber
        );
      }
    );

    return [...map.values()].sort((a, b) => a.registration.localeCompare(b.registration));
  }, [aircraft, maintenance, intimations, technicalLogBooks, sectors]);

  const selectedAircraft = useMemo(() => {
    if (!aircraftFilter || aircraftFilter === ALL_AIRCRAFT) return null;
    const wantedId = toId(aircraftFilter);
    const wantedReg = toReg(aircraftFilter);
    return (
      aircraftOptions.find((a) => a.id === wantedId) ||
      aircraftOptions.find((a) => toReg(a.registration) === wantedReg) ||
      null
    );
  }, [aircraftOptions, aircraftFilter]);

  const scopedSource = useMemo(() => {
    if (!aircraftFilter || aircraftFilter === ALL_AIRCRAFT) return fleetSource;
    const wantedId = selectedAircraft?.id || toId(aircraftFilter);
    const wantedReg = selectedAircraft?.registration || toReg(aircraftFilter);
    return filterSourceByAircraft(fleetSource, wantedId, wantedReg);
  }, [fleetSource, aircraftFilter, selectedAircraft]);

  const scopedRecordCounts = useMemo(() => {
    const alerts =
      (scopedSource.storeAlerts?.lowStock?.length || 0) +
      (scopedSource.storeAlerts?.expiringItems?.length || 0) +
      (scopedSource.storeAlerts?.calibrationDue?.length || 0);
    return {
      trips: scopedSource.trips?.length || 0,
      sectors: scopedSource.sectors?.length || 0,
      maintenance: scopedSource.maintenance?.length || 0,
      intimations: scopedSource.intimations?.length || 0,
      techlogs: scopedSource.technicalLogBooks?.length || 0,
      purchaseOrders: scopedSource.purchaseOrders?.length || 0,
      receiving: scopedSource.receiving?.length || 0,
      alerts
    };
  }, [scopedSource]);

  const departments = useMemo(() => buildDepartments(scopedSource), [scopedSource]);
  const active = departments[tab] || departments[0];
  const isFleetView = !aircraftFilter || aircraftFilter === ALL_AIRCRAFT;

  // Defer per-aircraft scoreboard so the main dashboard UI stays responsive.
  useEffect(() => {
    let cancelled = false;
    if (!aircraftOptions.length) {
      setAircraftScoreboard([]);
      setScoreboardLoading(false);
      return undefined;
    }
    setScoreboardLoading(true);
    const timer = setTimeout(() => {
      try {
        const rows = aircraftOptions.map((ac) => {
          const scoped = filterSourceByAircraft(fleetSource, ac.id, ac.registration);
          const depts = buildDepartments(scoped);
          const avg = Math.round(
            depts.reduce((s, d) => s + d.score, 0) / Math.max(depts.length, 1)
          );
          const meta = ratingMeta(avg);
          return {
            id: ac.id,
            registration: ac.registration,
            status: ac.status,
            avgScore: avg,
            label: meta.label,
            color: meta.color,
            deptScores: depts.map((d) => ({
              id: d.id,
              name: d.name,
              score: d.score,
              color: d.color
            }))
          };
        });
        if (!cancelled) setAircraftScoreboard(rows);
      } catch (err) {
        console.error('Failed to build aircraft scoreboard:', err);
        if (!cancelled) setAircraftScoreboard([]);
      } finally {
        if (!cancelled) setScoreboardLoading(false);
      }
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [aircraftOptions, fleetSource]);

  const comparisonData = useMemo(
    () =>
      departments.map((d) => ({
        department: d.name,
        score: d.score,
        fill: d.color,
        backlog: d.metrics.find((m) => /backlog|pending|queue|awaiting/i.test(m.label))?.value ?? 0
      })),
    [departments]
  );

  const radarData = useMemo(
    () =>
      departments.map((d) => ({
        department: d.name.split(' ')[0],
        score: d.score,
        fullMark: 100
      })),
    [departments]
  );

  const aircraftComparisonChart = useMemo(
    () =>
      aircraftScoreboard.map((row) => ({
        aircraft: row.registration,
        score: row.avgScore,
        fill: row.color
      })),
    [aircraftScoreboard]
  );

  const avgScore = Math.round(
    departments.reduce((s, d) => s + d.score, 0) / Math.max(departments.length, 1)
  );
  const avgMeta = ratingMeta(avgScore);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 3,
        borderRadius: 2.5,
        border: `1px solid ${THEME.border}`,
        bgcolor: THEME.card
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Build sx={{ color: THEME.gold, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
              Department-wise Analysis
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: THEME.muted }}>
            Operations, Flight Entry, CAMO, Planning, Procurement &amp; Store, and MRO — fleet-wide
            or by aircraft.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="dept-aircraft-filter-label">Aircraft</InputLabel>
            <Select
              labelId="dept-aircraft-filter-label"
              label="Aircraft"
              value={
                aircraftFilter === ALL_AIRCRAFT ||
                aircraftOptions.some((a) => a.id === aircraftFilter)
                  ? aircraftFilter
                  : ALL_AIRCRAFT
              }
              renderValue={(value) => {
                if (value === ALL_AIRCRAFT) return 'All aircraft (fleet)';
                const match = aircraftOptions.find((a) => a.id === value);
                return match?.registration || 'Aircraft';
              }}
              onChange={(e) => {
                setAircraftFilter(String(e.target.value));
                setTab(0);
              }}
            >
              <MenuItem value={ALL_AIRCRAFT}>All aircraft (fleet)</MenuItem>
              {aircraftOptions.map((ac) => (
                <MenuItem key={ac.id} value={ac.id}>
                  {ac.registration}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip
            label={`${isFleetView ? 'Fleet' : selectedAircraft?.registration || 'Aircraft'} avg ${avgScore} · ${avgMeta.label}`}
            sx={{
              fontWeight: 700,
              bgcolor: alpha(avgMeta.color, 0.12),
              color: avgMeta.color,
              border: `1px solid ${alpha(avgMeta.color, 0.35)}`
            }}
          />
          {(analysisLoading || scoreboardLoading) && (
            <Chip
              icon={<CircularProgress size={14} />}
              label={analysisLoading ? 'Loading analysis data…' : 'Building aircraft scores…'}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>
      </Stack>

      {isFleetView && (scoreboardLoading || aircraftScoreboard.length > 0) && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${THEME.border}`,
                bgcolor: alpha(THEME.mist, 0.45)
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
                Aircraft performance comparison
              </Typography>
              <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mb: 1.5 }}>
                Average department rating per aircraft. Click a row in the table to drill into that
                aircraft.
              </Typography>
              <Box sx={{ height: Math.max(220, Math.min(360, Math.max(aircraftComparisonChart.length, 1) * 36)) }}>
                {scoreboardLoading && aircraftComparisonChart.length === 0 ? (
                  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                    <CircularProgress size={28} sx={{ color: THEME.steel }} />
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={aircraftComparisonChart}
                    layout="vertical"
                    margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={THEME.border} horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      tick={{ fill: THEME.muted, fontSize: 11 }}
                      axisLine={{ stroke: THEME.border }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="aircraft"
                      width={88}
                      tick={{ fill: THEME.ink, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip content={<ChartTooltip />} />
                    <Bar dataKey="score" name="Avg rating" radius={[0, 6, 6, 0]} maxBarSize={18}>
                      {aircraftComparisonChart.map((entry) => (
                        <Cell key={entry.aircraft} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${THEME.border}`,
                bgcolor: alpha(THEME.mist, 0.45)
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: THEME.ink, mb: 1 }}>
                Aircraft scoreboard
              </Typography>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Aircraft</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Avg
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Rating</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aircraftScoreboard.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setAircraftFilter(row.id);
                          setTab(0);
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: THEME.ink }}>
                          {row.registration}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: row.color }}>
                          {row.avgScore}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.label}
                            sx={{
                              fontWeight: 600,
                              bgcolor: alpha(row.color, 0.12),
                              color: row.color
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {!isFleetView && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${alpha(THEME.steel, 0.3)}`,
            background: `linear-gradient(120deg, ${alpha(THEME.steel, 0.08)} 0%, ${THEME.card} 70%)`
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <AirplanemodeActive sx={{ color: THEME.steel }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: THEME.ink }}>
              Aircraft view: {selectedAircraft?.registration || aircraftFilter}
            </Typography>
            <Chip
              size="small"
              label="Back to fleet"
              onClick={() => setAircraftFilter(ALL_AIRCRAFT)}
              sx={{ fontWeight: 600 }}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mt: 0.5 }}>
            Scoped records — trips {scopedRecordCounts.trips}, sectors {scopedRecordCounts.sectors},
            WOs {scopedRecordCounts.maintenance}, intimations {scopedRecordCounts.intimations},
            techlogs {scopedRecordCounts.techlogs},
            POs {scopedRecordCounts.purchaseOrders}, receiving {scopedRecordCounts.receiving},
            store alerts {scopedRecordCounts.alerts}.
          </Typography>
          {selectedAircraft && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {aircraftScoreboard
                .find((r) => r.id === selectedAircraft.id)
                ?.deptScores.map((d) => (
                  <Chip
                    key={d.id}
                    size="small"
                    label={`${d.name}: ${d.score}`}
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(d.color, 0.12),
                      color: d.color,
                      border: `1px solid ${alpha(d.color, 0.28)}`
                    }}
                  />
                ))}
            </Stack>
          )}
        </Paper>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        {departments.map((d) => (
          <Grid item xs={6} sm={4} md={2} key={d.id}>
            <Paper
              elevation={0}
              onClick={() => setTab(departments.findIndex((x) => x.id === d.id))}
              sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                cursor: 'pointer',
                border: `1px solid ${
                  active?.id === d.id ? alpha(d.color, 0.55) : THEME.border
                }`,
                background: `linear-gradient(160deg, ${THEME.card} 0%, ${alpha(d.color, 0.08)} 100%)`,
                transition: 'transform 0.15s ease, border-color 0.15s ease',
                '&:hover': { transform: 'translateY(-1px)' }
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                <Box sx={{ color: d.color, display: 'grid', placeItems: 'center' }}>{d.icon}</Box>
                <Typography
                  variant="caption"
                  sx={{ color: THEME.ink, fontWeight: 700, lineHeight: 1.2 }}
                >
                  {d.name}
                </Typography>
              </Stack>
              <Typography
                variant="h5"
                sx={{
                  color: d.color,
                  fontWeight: 800,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1
                }}
              >
                {d.score}
              </Typography>
              <Typography variant="caption" sx={{ color: d.color, fontWeight: 600 }}>
                {d.label}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={d.score}
                sx={{
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: alpha(d.color, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: d.color, borderRadius: 3 }
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              border: `1px solid ${THEME.border}`,
              bgcolor: alpha(THEME.mist, 0.45)
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
              Performance rating by department
              {!isFleetView ? ` · ${selectedAircraft?.registration || ''}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mb: 1.5 }}>
              100 = healthy throughput with low critical backlog. Deductions for queues and alerts.
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={THEME.border} />
                  <XAxis
                    dataKey="department"
                    tick={{ fill: THEME.ink, fontSize: 11 }}
                    axisLine={{ stroke: THEME.border }}
                    tickLine={false}
                    interval={0}
                    angle={-18}
                    textAnchor="end"
                    height={56}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: THEME.muted, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Bar dataKey="score" name="Rating" radius={[6, 6, 0, 0]} maxBarSize={42}>
                    {comparisonData.map((entry) => (
                      <Cell key={entry.department} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              border: `1px solid ${THEME.border}`,
              bgcolor: alpha(THEME.mist, 0.45)
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
              Balanced score profile
            </Typography>
            <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mb: 1.5 }}>
              Radar view of departmental health
              {isFleetView ? ' across the organisation.' : ' for this aircraft.'}
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke={THEME.border} />
                  <PolarAngleAxis dataKey="department" tick={{ fill: THEME.ink, fontSize: 11 }} />
                  <PolarRadiusAxis
                    angle={30}
                    domain={[0, 100]}
                    tick={{ fill: THEME.muted, fontSize: 10 }}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke={THEME.navy}
                    fill={THEME.steel}
                    fillOpacity={0.35}
                  />
                  <RechartsTooltip content={<ChartTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          minHeight: 40,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 40,
            color: THEME.muted
          },
          '& .Mui-selected': { color: `${THEME.ink} !important` },
          '& .MuiTabs-indicator': { bgcolor: THEME.gold, height: 3, borderRadius: 2 }
        }}
      >
        {departments.map((d) => (
          <Tab key={d.id} icon={d.icon} iconPosition="start" label={d.name} />
        ))}
      </Tabs>

      {active && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${alpha(active.color, 0.35)}`,
                background: `linear-gradient(165deg, ${THEME.card} 0%, ${alpha(active.color, 0.08)} 100%)`
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="overline" sx={{ color: THEME.muted, letterSpacing: 1 }}>
                    Performance rating
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      color: active.color,
                      fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.05
                    }}
                  >
                    {active.score}
                  </Typography>
                  <Chip
                    size="small"
                    label={active.label}
                    sx={{
                      mt: 0.75,
                      fontWeight: 700,
                      bgcolor: alpha(active.color, 0.14),
                      color: active.color
                    }}
                  />
                </Box>
                <Box sx={{ color: active.color }}>{active.icon}</Box>
              </Stack>
              <Typography variant="body2" sx={{ color: THEME.muted, mt: 1.5, mb: 1.5 }}>
                {active.scope}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={active.score}
                sx={{
                  mb: 2,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: alpha(active.color, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: active.color, borderRadius: 4 }
                }}
              />
              {active.metrics.map((m) => (
                <MetricRow key={m.label} label={m.label} value={m.value} accent={active.color} />
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${THEME.border}`
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
                Status composition
              </Typography>
              <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mb: 1 }}>
                Mix of open and closed work for {active.name}.
              </Typography>
              <StatusPie data={active.statusPie} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${THEME.border}`
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
                Workload by lane
              </Typography>
              <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mb: 1 }}>
                Queue and throughput lanes driving the rating.
              </Typography>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={active.barMetrics}
                    layout="vertical"
                    margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={THEME.border} horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: THEME.muted, fontSize: 11 }}
                      axisLine={{ stroke: THEME.border }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={110}
                      tick={{ fill: THEME.ink, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: alpha(THEME.steel, 0.06) }} />
                    <Bar dataKey="value" name="Count" radius={[0, 6, 6, 0]} maxBarSize={20}>
                      {active.barMetrics.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default DepartmentWiseAnalysis;
